import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle2, XCircle, Clock, Download } from 'lucide-react';
import { publicQuotesApi } from '../ops/api';
import { QuoteDocumentView } from '../components/QuoteDocumentView';
import { BUILDBROZ_LOGO_URL } from '../branding';

const Brand = () => (
  <div className="flex items-center gap-3">
    <img src={BUILDBROZ_LOGO_URL} alt="BuildBroz" className="h-11 w-11 object-contain mix-blend-screen" />
    <div>
      <div className="text-white font-brand font-black text-lg tracking-tight">BUILD<span className="text-[#D4AF37]">BROZ</span></div>
      <div className="text-[9px] text-gray-400 uppercase tracking-[0.25em] font-brand">Construction &amp; Remodeling</div>
    </div>
  </div>
);

const Shell = ({ children, showHeader = true }) => (
  <div className="min-h-screen bg-[#0A0A0A]" data-testid="public-quote-page">
    {showHeader && (
      <header className="border-b border-gray-800 bg-black">
        <div className="max-w-4xl mx-auto px-4 py-4"><Brand /></div>
      </header>
    )}
    <main className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-10">{children}</main>
    <footer className="max-w-4xl mx-auto px-4 pb-10 text-center text-xs text-gray-600">
      Questions about this quote? Contact BuildBroz directly.
    </footer>
  </div>
);

const MessageCard = ({ title, message, testId }) => (
  <div className="bg-[#141414] border border-gray-800 rounded-lg p-8 text-center" data-testid={testId}>
    <Clock size={32} className="mx-auto text-[#D4AF37] mb-3" />
    <h1 className="text-white text-lg font-semibold mb-2">{title}</h1>
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);

const inputCls = 'bg-white border-gray-300 text-gray-900 focus:border-[#D4AF37]';

const ApproveDialog = ({ open, onOpenChange, token, quoteNumber, onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setName(''); setEmail(''); setAck(false); setError(null); } }, [open]);

  const submit = async () => {
    if (!name.trim()) return setError('Please enter your name.');
    if (!ack) return setError('Please check the acknowledgement box to continue.');
    setBusy(true); setError(null);
    try {
      const r = await publicQuotesApi.approve(token, { name: name.trim(), email: email.trim() || null, acknowledged: true });
      onOpenChange(false);
      onSuccess(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="bg-white text-gray-900 max-w-md" data-testid="approve-dialog">
        <DialogHeader><DialogTitle className="text-gray-900">Approve Quote {quoteNumber}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="approve-name-input" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Email (optional)</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inputCls} data-testid="approve-email-input" />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="approve-ack-checkbox" />
            <span>I have reviewed this quote and approve the scope and price shown above.</span>
          </label>
          {error && <p className="text-sm text-red-600" data-testid="approve-error">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-300 text-gray-700" data-testid="approve-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-green-600 hover:bg-green-500 text-white font-semibold" data-testid="approve-confirm-btn">
              {busy && <Loader2 size={14} className="animate-spin" />} Approve Quote
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeclineDialog = ({ open, onOpenChange, token, quoteNumber, onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setName(''); setEmail(''); setReason(''); setError(null); } }, [open]);

  const submit = async () => {
    if (!name.trim()) return setError('Please enter your name.');
    setBusy(true); setError(null);
    try {
      const r = await publicQuotesApi.decline(token, { name: name.trim(), email: email.trim() || null, reason: reason.trim() || null });
      onOpenChange(false);
      onSuccess(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="bg-white text-gray-900 max-w-md" data-testid="decline-dialog">
        <DialogHeader><DialogTitle className="text-gray-900">Decline Quote {quoteNumber}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="decline-name-input" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Email (optional)</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inputCls} data-testid="decline-email-input" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Reason (optional)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={inputCls} data-testid="decline-reason-input" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="decline-error">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-300 text-gray-700" data-testid="decline-cancel-btn">Go Back</Button>
            <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-500 text-white font-semibold" data-testid="decline-confirm-btn">
              {busy && <Loader2 size={14} className="animate-spin" />} Decline Quote
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function PublicQuote() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, notFound: false, data: null });
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [responded, setResponded] = useState(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    publicQuotesApi.get(token)
      .then((r) => {
        setState({ loading: false, notFound: false, data: r.data });
        if (r.data.available && !viewedRef.current) {
          viewedRef.current = true;
          publicQuotesApi.viewed(token).catch(() => {});
        }
      })
      .catch(() => setState({ loading: false, notFound: true, data: null }));
  }, [token]);

  if (state.loading) return <Shell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div></Shell>;
  if (state.notFound) return <Shell><MessageCard title="Quote Not Found" message="This link is not valid. Please contact BuildBroz if you believe this is an error." testId="public-quote-not-found" /></Shell>;
  if (!state.data.available) {
    return state.data.gone
      ? <Shell><MessageCard title="This Quote No Longer Exists" message="This quote has been removed. Please contact BuildBroz if you have any questions." testId="public-quote-gone" /></Shell>
      : <Shell><MessageCard title="Quote Unavailable" message={state.data.message} testId="public-quote-unavailable" /></Shell>;
  }

  const q = state.data.quote;

  if (responded?.status === 'accepted') {
    return (
      <Shell>
        <div className="bg-white rounded-lg p-8 text-center shadow-xl" data-testid="approve-success">
          <CheckCircle2 size={48} className="mx-auto text-green-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Quote Approved</h1>
          <p className="text-gray-600 mb-6">Thank you. Your quote has been approved.</p>
          <div className="inline-block text-left text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-4">
            <p><span className="text-gray-500">Quote Number:</span> <b>{responded.quote_number}</b></p>
            <p><span className="text-gray-500">Approved:</span> {new Date(responded.accepted_at).toLocaleDateString()}</p>
            <p><span className="text-gray-500">Approved by:</span> {responded.accepted_by_customer_name}</p>
          </div>
        </div>
      </Shell>
    );
  }
  if (responded?.status === 'declined') {
    return (
      <Shell>
        <div className="bg-white rounded-lg p-8 text-center shadow-xl" data-testid="decline-success">
          <XCircle size={48} className="mx-auto text-gray-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Response Recorded</h1>
          <p className="text-gray-600 mb-6">Your response has been recorded.</p>
          <div className="inline-block text-left text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-4">
            <p><span className="text-gray-500">Quote Number:</span> <b>{responded.quote_number}</b></p>
            <p><span className="text-gray-500">Response date:</span> {new Date(responded.declined_at).toLocaleDateString()}</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell showHeader={false}>
      <div className="flex justify-end mb-3">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/quotes/${token}/pdf`} download
          className="inline-flex items-center gap-2 bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] hover:from-[#D4AF37] hover:to-[#B8941F] text-black font-brand font-extrabold uppercase tracking-wide text-xs px-4 py-2.5 rounded-lg transition-colors"
          data-testid="download-quote-pdf-btn">
          <Download size={14} /> Download PDF
        </a>
      </div>
      <QuoteDocumentView q={q} onApprove={() => setApproveOpen(true)} onDecline={() => setDeclineOpen(true)} />

      <ApproveDialog open={approveOpen} onOpenChange={setApproveOpen} token={token} quoteNumber={q.quote_number} onSuccess={setResponded} />
      <DeclineDialog open={declineOpen} onOpenChange={setDeclineOpen} token={token} quoteNumber={q.quote_number} onSuccess={setResponded} />
    </Shell>
  );
}
